/**
 * ConfirmDialog — in-app stand-in for window.confirm / window.prompt /
 * window.alert (BACKLOG-1297).
 *
 * ROP-teams only ever runs inside the Teams desktop + iOS clients, and that
 * webview silently suppresses native dialogs: `window.confirm()` returns
 * without painting anything, so a "are you sure?" guard simply never happens
 * and the FSR has no console to notice it in. Every such call site routes
 * through this dialog instead.
 *
 * Look + ceremony match PreSubmitConfirmModal: ModalShell body copy, ghost
 * cancel / tone-coloured commit in the footer. Tokens only, no literal hex.
 *
 * Most callers want the promise hooks in ConfirmProvider rather than mounting
 * this directly — they convert a call site in place:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Remove?', message: '…', tone: 'danger' }))) return;
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ModalShell } from './ModalShell';
import { Button } from './Button';

/** Commit-button tone. 'danger' = destructive/irreversible. */
export type ConfirmTone = 'primary' | 'danger';

/** Optional single text field (the window.prompt replacement). */
export interface ConfirmInputSpec {
  /** Field label — also the accessible name. */
  label: string;
  placeholder?: string;
  /** Pre-filled value (window.prompt's second argument). */
  defaultValue?: string;
  /** When set, the commit button stays disabled until the value is long enough. */
  minLength?: number;
  /** Shorthand for minLength 1 — a value is mandatory. */
  required?: boolean;
  /** Render a textarea instead of an input (longer free text). */
  multiline?: boolean;
  /** 'password' masks the value (admin keys). Ignored when multiline. */
  type?: 'text' | 'password';
  /** Small helper line under the field. */
  hint?: string;
}

export interface ConfirmOptions {
  title: string;
  /** Body copy. ReactNode so a caller can bold a name or add a second line. */
  message?: ReactNode;
  /** Commit-button label. Defaults to 'Confirm' ('OK' when acknowledge-only). */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to 'Cancel'. */
  cancelLabel?: string;
  tone?: ConfirmTone;
  /**
   * Acknowledge-only mode — the window.alert replacement. One button, no
   * cancel; the promise resolves confirmed either way.
   */
  acknowledgeOnly?: boolean;
  input?: ConfirmInputSpec;
}

/** ConfirmOptions with the text field made mandatory (usePrompt). */
export interface PromptOptions extends ConfirmOptions {
  input: ConfirmInputSpec;
}

export interface ConfirmOutcome {
  /** True when the commit button was used (always true for acknowledge-only). */
  confirmed: boolean;
  /** Text-field value, '' when the dialog has no field. */
  value: string;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  /** Fired once, with the outcome, for every close path. */
  onResolve: (outcome: ConfirmOutcome) => void;
}

/** Primitive: ConfirmDialog. */
export function ConfirmDialog({
  open,
  onResolve,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'primary',
  acknowledgeOnly = false,
  input,
}: ConfirmDialogProps): JSX.Element | null {
  const [value, setValue] = useState<string>(input?.defaultValue ?? '');
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const resolveRef = useRef(onResolve);
  const fieldId = useId();
  useEffect(() => { resolveRef.current = onResolve; }, [onResolve]);

  const minLength = input ? (input.minLength ?? (input.required ? 1 : 0)) : 0;
  const tooShort = value.trim().length < minLength;
  const canCommit = !input || !tooShort;

  // Escape handling lives here rather than in ModalShell because this dialog
  // is normally stacked ON TOP of another ModalShell, and ModalShell's Escape
  // listener sits on `document` — so one keypress would close both. A capture
  // listener on window runs first and stops the event before it reaches any
  // of them.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      resolveRef.current({ confirmed: acknowledgeOnly, value: '' });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, acknowledgeOnly]);

  // ModalShell focuses the header X on open; when we're collecting text the
  // field is what the operator wants. Deferred a tick so it wins that race.
  useEffect(() => {
    if (!open || !input) return;
    const t = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, input]);

  if (!open) return null;

  const commit = (): void => {
    if (!canCommit) return;
    onResolve({ confirmed: true, value: value.trim() });
  };
  const cancel = (): void => onResolve({ confirmed: acknowledgeOnly, value: '' });

  // Clicks inside this dialog must not reach document-level "clicked outside,
  // so close me" listeners on the surface underneath (the PDW doc-preview
  // drawer is one). ModalShell portals onto document.body, but React still
  // bubbles portal events through this tree, so a display:contents wrapper
  // here catches them before they get back out to document.
  const stopPointer = (e: { stopPropagation: () => void }): void => e.stopPropagation();

  const fieldStyle = {
    width: '100%',
    padding: 'var(--sp-3) var(--sp-4)',
    borderRadius: 'var(--r-sm)',
    border: `1px solid ${tooShort && value.length > 0 ? 'var(--pcb-red)' : 'var(--ink-300)'}`,
    background: 'var(--surface-card)',
    color: 'var(--ink-900)',
    fontFamily: 'inherit',
    fontSize: 13,
  } as const;

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      {message && (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--ink-600)' }}>
          {message}
        </p>
      )}
      {input && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label
            htmlFor={fieldId}
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-800)' }}
          >
            {input.label}
          </label>
          {input.multiline ? (
            <textarea
              id={fieldId}
              ref={(el) => { fieldRef.current = el; }}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={input.placeholder ?? ''}
              rows={3}
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          ) : (
            <input
              id={fieldId}
              ref={(el) => { fieldRef.current = el; }}
              type={input.type ?? 'text'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={input.placeholder ?? ''}
              // Enter commits, like window.prompt's OK.
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
              style={fieldStyle}
            />
          )}
          {input.hint && (
            <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>{input.hint}</span>
          )}
          {minLength > 0 && tooShort && (
            <span style={{ fontSize: 11, color: 'var(--pcb-red)' }}>
              {minLength === 1
                ? 'This is required.'
                : `Enter at least ${minLength} characters.`}
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{ display: 'contents' }}
      onMouseDown={stopPointer}
      onPointerDown={stopPointer}
      onTouchStart={stopPointer}
    >
      <ModalShell
        open
        onClose={cancel}
        title={title}
        size="sm"
        // Escape is handled above (stacked-modal problem); the scrim stays
        // click-to-dismiss because that reads as "cancel" everywhere else.
        disableEscapeClose
        footer={
          <>
            {!acknowledgeOnly && (
              <Button variant="ghost" onClick={cancel}>
                {cancelLabel}
              </Button>
            )}
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              onClick={commit}
              disabled={!canCommit}
            >
              {confirmLabel ?? (acknowledgeOnly ? 'OK' : 'Confirm')}
            </Button>
          </>
        }
      >
        {body}
      </ModalShell>
    </div>
  );
}
